import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X, Sparkles, Loader2, CheckCircle2, AlertCircle,
  ShoppingCart, Lightbulb, Megaphone, ArrowRight, ExternalLink,
  Globe, Shield, MessageSquare, Zap, Layout, ChevronRight
} from 'lucide-react';
import { generateBusinessSuggestions, BusinessIdea, BusinessSuggestion } from '@/services/geminiApi';
import { searchDomains, DomainSearchResult } from '@/services/dreamscapeApi';
import { addDomainToCart } from '@/utils/cart';
import { convertToUSD, convertCurrency, formatPriceWithCurrency } from '@/utils/currencyConverter';
import { AnimatedButton } from '@/components/common/animations';
import { GradientButton } from '@/components/common/ui/Button';

interface AISuggestionFormProps {
  isOpen: boolean;
  onClose: () => void;
}

function ServiceIcon({ path, color }: { path: string; color: string }) {
  const IconComp = path.includes('email') ? MessageSquare
    : path.includes('marketing') ? Megaphone
      : path.includes('website') ? Globe
        : path.includes('hosting') ? Layout
          : path.includes('server') ? Layout
            : path.includes('design') ? Layout
              : path.includes('cyber') ? Shield
                : Zap;
  return (
    <div
      className="flex items-center justify-center shrink-0 rounded-lg"
      style={{ width: 30, height: 30, background: `${color}18`, border: `1px solid ${color}28` }}
    >
      <IconComp size={14} color={color} />
    </div>
  );
}

function InfoPopup({ isOpen, onClose, title, icon: Icon, items, color = 'violet', relatedServices, onServiceClick }: {
  isOpen: boolean; onClose: () => void; title: string; icon: any;
  items: string[]; color?: 'violet' | 'cyan' | 'green';
  relatedServices?: any[]; onServiceClick?: (path: string) => void;
}) {
  if (!isOpen) return null;

  const palette = {
    violet: { accent: '#a78bfa', border: 'rgba(139,92,246,0.3)', glow: 'rgba(139,92,246,0.12)' },
    cyan: { accent: '#67e8f9', border: 'rgba(34,211,238,0.3)', glow: 'rgba(34,211,238,0.12)' },
    green: { accent: '#6ee7b7', border: 'rgba(52,211,153,0.3)', glow: 'rgba(52,211,153,0.12)' },
  }[color];

  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-5"
      style={{ zIndex: 99999, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      onWheel={(e) => e.stopPropagation()}
    >
      <div
        className="relative w-full flex flex-col rounded-[20px]"
        style={{
          maxWidth: 560, height: '80vh',
          background: 'linear-gradient(145deg, #111128, #0a0a18)',
          border: `1px solid ${palette.border}`,
          boxShadow: `0 0 0 1px rgba(255,255,255,0.04), 0 32px 80px rgba(0,0,0,0.7), 0 0 60px ${palette.glow}`,
        }}
        onClick={(e) => e.stopPropagation()}
        onWheel={(e) => e.stopPropagation()}
      >
        <div
          className="absolute top-0 rounded-sm"
          style={{
            left: '20%', right: '20%', height: 1,
            background: `linear-gradient(90deg, transparent, ${palette.accent}, transparent)`,
          }}
        />

        <div className="flex items-center justify-between px-5 py-[18px] shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="flex items-center gap-[10px]">
            <div
              className="flex items-center justify-center rounded-[9px]"
              style={{ width: 34, height: 34, background: `${palette.accent}18`, border: `1px solid ${palette.border}` }}
            >
              <Icon size={16} color={palette.accent} />
            </div>
            <span className="text-[15px] font-bold" style={{ color: palette.accent }}>{title}</span>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-[30px] h-[30px] rounded-lg cursor-pointer transition-colors hover:bg-white/10"
            style={{ border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.05)' }}
          >
            <X size={14} color="#9ca3af" />
          </button>
        </div>

        <div
          className="flex-1 overflow-y-auto p-4 min-h-0"
          style={{ overscrollBehavior: 'contain', scrollbarWidth: 'thin' }}
        >
          <div className="flex flex-col gap-2">
            {items.map((item, i) => (
              <div
                key={i}
                className="flex gap-[10px] items-start px-[14px] py-3 rounded-[10px]"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
              >
                <span
                  className="flex items-center justify-center text-[11px] font-bold shrink-0 rounded-[5px]"
                  style={{ minWidth: 20, height: 20, background: `${palette.accent}18`, color: palette.accent }}
                >{i + 1}</span>
                <p className="m-0 text-[#d1d5db] text-[13px] leading-relaxed">{item}</p>
              </div>
            ))}
          </div>

          {relatedServices && relatedServices.length > 0 && (
            <div className="mt-6 pt-5" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
              <p className="m-0 mb-3 text-[11px] font-semibold text-[#6b7280] tracking-widest uppercase">Related Services</p>
              <div className="flex flex-col gap-[7px]">
                {relatedServices.map((service, i) => (
                  <button
                    key={i}
                    onClick={() => onServiceClick?.(service.path)}
                    className="flex items-center gap-[10px] px-[14px] py-[10px] rounded-[9px] cursor-pointer text-left w-full transition-all hover:border-white/15 hover:bg-white/[0.04]"
                    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
                  >
                    <ServiceIcon path={service.path} color={palette.accent} />
                    <div className="flex-1">
                      <p className="m-0 mb-0.5 text-[#f3f4f6] text-[12px] font-semibold">{service.title}</p>
                      <p className="m-0 text-[#6b7280] text-[11px]">{service.description}</p>
                    </div>
                    <ExternalLink size={12} color="#374151" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AISuggestionForm({ isOpen, onClose }: AISuggestionFormProps) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<BusinessIdea>({
    concept: '', industry: '', targetAudience: '', additionalInfo: '',
  });
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<BusinessSuggestion | null>(null);
  const [domainPricing, setDomainPricing] = useState<Record<string, DomainSearchResult>>({});
  const [loadingPricing, setLoadingPricing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [showMarketingTips, setShowMarketingTips] = useState(false);
  const [showNextSteps, setShowNextSteps] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.concept.trim()) { setError('Please enter your business concept'); return; }
    setLoading(true); setError(null); setSuggestions(null); setDomainPricing({});
    try {
      const result = await generateBusinessSuggestions(formData);
      if (result.success && result.data) {
        setSuggestions(result.data);
        await fetchDomainPricing(result.data.domainSuggestions);
      } else {
        setError(result.message || 'Failed to generate suggestions. Please try again.');
      }
    } catch {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchDomainPricing = async (domainSuggestions: { domain: string; reasoning: string; tld: string }[]) => {
    setLoadingPricing(true);
    const pricingMap: Record<string, DomainSearchResult> = {};
    try {
      const domains = domainSuggestions.map(s => s.domain);
      if (domains.length === 0) { setLoadingPricing(false); return; }
      const baseName = domains[0].split('.').slice(0, -1).join('.');
      const response = await searchDomains(baseName, [], domains, 'USD');
      if (response.success && response.results) {
        response.results.forEach(r => { pricingMap[r.domain.toLowerCase()] = r; });
      }
    } catch (err) {
      console.error('Error fetching domain pricing:', err);
    } finally {
      setLoadingPricing(false);
      setDomainPricing(pricingMap);
    }
  };

  const handleBuyDomain = (domain: string) => {
    if (import.meta.env.VITE_USE_DB === 'false') {
      const storefrontUrl = import.meta.env.VITE_DREAMSCAPE_STOREFRONT_URL || 'https://sandomains.com';
      const searchUrl = `${storefrontUrl.replace(/\/$/, '')}/domain-names/search/?domain=${encodeURIComponent(domain)}`;
      window.open(searchUrl, '_blank');
      return;
    }

    const pricing = domainPricing[domain.toLowerCase()];
    if (!pricing || !pricing.available) { setError('This domain is not available for purchase.'); return; }
    addDomainToCart({
      name: domain, price: pricing.registerPrice || 0,
      currency: pricing.currency || 'USD', renewPrice: pricing.renewPrice,
      available: pricing.available, period: 1, privacy: true,
    });
    handleClose();
  };

  const handleClose = () => {
    setFormData({ concept: '', industry: '', targetAudience: '', additionalInfo: '' });
    setSuggestions(null); setDomainPricing({}); setError(null);
    setShowRecommendations(false); setShowMarketingTips(false); setShowNextSteps(false);
    onClose();
  };

  // ✅ Removed body overflow manipulation entirely — useFullPageScroll manages that
  // Modal scroll is contained via onWheel stopPropagation on the backdrop and modal

  if (!isOpen) return null;

  const inputClass = `
    w-full box-border px-[14px] py-[10px]
    bg-white/[0.04] border border-white/10
    rounded-[10px] text-[#f3f4f6] text-sm
    outline-none resize-none
    transition-all duration-200 placeholder-white/25
    focus:border-violet-500/60 focus:shadow-[0_0_0_3px_rgba(139,92,246,0.1)]
  `;

  return (
    <>
      <style>{`
        @keyframes fadeOverlay { from{opacity:0} to{opacity:1} }
        @keyframes slideModal {
          from{opacity:0;transform:translateY(16px) scale(0.97)}
          to  {opacity:1;transform:translateY(0) scale(1)}
        }
        @keyframes spinLoader { to{transform:rotate(360deg)} }
        @keyframes fadeSlide {
          from{opacity:0;transform:translateY(8px)}
          to  {opacity:1;transform:translateY(0)}
        }
        .ai-scroll::-webkit-scrollbar{width:4px}
        .ai-scroll::-webkit-scrollbar-track{background:transparent}
        .ai-scroll::-webkit-scrollbar-thumb{background:rgba(139,92,246,0.35);border-radius:4px}
        .spin{animation:spinLoader 1s linear infinite}
        .fade-slide-0{animation:fadeSlide 0.3s ease 0s both}
        .fade-slide-1{animation:fadeSlide 0.3s ease 0.06s both}
        .fade-slide-2{animation:fadeSlide 0.3s ease 0.12s both}
        .fade-slide-3{animation:fadeSlide 0.3s ease 0.18s both}
        .fade-slide-4{animation:fadeSlide 0.3s ease 0.24s both}
      `}</style>

      {/* BACKDROP — stopPropagation prevents wheel events reaching the page scroll hook */}
      <div
        className="fixed inset-0 flex items-center justify-center p-4"
        style={{
          zIndex: 9999,
          background: 'rgba(0,0,0,0.4)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          animation: 'fadeOverlay 0.2s ease',
        }}
        onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
        onWheel={(e) => e.stopPropagation()} // ✅ stop wheel reaching hook
      >
        {/* MODAL */}
        <div
          className="relative w-full flex flex-col overflow-hidden rounded-[22px]"
          style={{
            maxWidth: 700,
            height: '76vh',
            background: 'linear-gradient(155deg, #0e0e22 0%, #090914 100%)',
            border: '1px solid rgba(139,92,246,0.22)',
            boxShadow: '0 0 0 1px rgba(255,255,255,0.03), 0 40px 100px rgba(0,0,0,0.7), 0 0 80px rgba(109,40,217,0.1)',
            animation: 'slideModal 0.28s cubic-bezier(0.34,1.3,0.64,1)',
          }}
          onClick={(e) => e.stopPropagation()}
          onWheel={(e) => e.stopPropagation()} // ✅ stop wheel reaching hook
        >
          {/* accent top line */}
          <div
            className="absolute top-0 h-px"
            style={{
              left: '20%', right: '20%',
              background: 'linear-gradient(90deg,transparent,rgba(167,139,250,0.9),rgba(103,232,249,0.5),transparent)',
            }}
          />
          {/* dot grid bg */}
          <div
            className="absolute inset-0 pointer-events-none opacity-[0.018]"
            style={{
              backgroundImage: 'radial-gradient(circle,rgba(139,92,246,1) 1px,transparent 1px)',
              backgroundSize: '28px 28px',
            }}
          />

          {/* HEADER */}
          <div
            className="flex items-center justify-between px-[22px] py-4 shrink-0 relative z-10"
            style={{
              borderBottom: '1px solid rgba(255,255,255,0.06)',
              background: 'rgba(139,92,246,0.03)',
            }}
          >
            <div className="flex items-center gap-3">
              <div
                className="flex items-center justify-center w-12 h-12 rounded-[11px]"
                style={{
                  background: 'linear-gradient(135deg,rgba(139,92,246,0.3),rgba(34,211,238,0.15))',
                  border: '1px solid rgba(139,92,246,0.35)',
                  boxShadow: '0 0 16px rgba(139,92,246,0.2)',
                }}
              >
                <Sparkles size={20} color="#c4b5fd" />
              </div>
              <div>
                <h2
                  className="m-0 text-[18px] font-bold tracking-tight"
                  style={{
                    background: 'linear-gradient(90deg,#c4b5fd 30%,#67e8f9)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >AI Business Suggestion</h2>
                <p className="m-0 mt-0.5 text-sm text-gray-300">
                  Personalized domain &amp; business recommendations
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="flex items-center justify-center w-8 h-8 rounded-lg cursor-pointer transition-colors hover:bg-white/10"
              style={{ border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.05)' }}
              aria-label="Close"
            >
              <X size={15} color="#9ca3af" />
            </button>
          </div>

          {/* BODY */}
          <div
            className="ai-scroll flex-1 overflow-y-auto px-8 py-[30px] min-h-0 relative z-10"
            style={{ overscrollBehavior: 'contain' }}
          >
            {!suggestions ? (
              /* ── FORM ── */
              <form onSubmit={handleSubmit} className="flex flex-col gap-[22px]">
                <div>
                  <label className="block text-[12px] font-semibold tracking-[0.07em] uppercase text-[#9ca3af] mb-2">
                    Business Concept <span className="text-violet-600">*</span>
                  </label>
                  <textarea
                    value={formData.concept}
                    onChange={(e) => setFormData({ ...formData, concept: e.target.value })}
                    placeholder="Describe your business idea..."
                    className={inputClass}
                    style={{ minHeight: 88 }}
                    rows={3}
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[12px] font-semibold tracking-[0.07em] uppercase text-[#9ca3af] mb-2">
                      Industry <span className="text-[#4b5563] normal-case text-[12px] font-normal tracking-normal">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={formData.industry}
                      onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                      placeholder="e.g., Technology, Healthcare..."
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className="block text-[12px] font-semibold tracking-[0.07em] uppercase text-[#9ca3af] mb-2">
                      Target Audience <span className="text-[#4b5563] normal-case text-[12px] font-normal tracking-normal">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={formData.targetAudience}
                      onChange={(e) => setFormData({ ...formData, targetAudience: e.target.value })}
                      placeholder="e.g., Small businesses, Students..."
                      className={inputClass}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[12px] font-semibold tracking-[0.07em] uppercase text-[#9ca3af] mb-2">
                    Additional Info <span className="text-[#4b5563] normal-case text-[12px] font-normal tracking-normal">(optional)</span>
                  </label>
                  <textarea
                    value={formData.additionalInfo}
                    onChange={(e) => setFormData({ ...formData, additionalInfo: e.target.value })}
                    placeholder="Any extra details..."
                    className={inputClass}
                    style={{ minHeight: 64 }}
                    rows={2}
                  />
                </div>

                {error && (
                  <div className="flex items-center gap-2 px-[14px] py-[10px] rounded-[9px] text-[#f87171] text-[13px]"
                    style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)' }}>
                    <AlertCircle size={14} /> {error}
                  </div>
                )}

                <GradientButton
                  type="submit"
                  disabled={loading}
                  className="w-full mt-1"
                >
                  {loading
                    ? <><Loader2 size={15} className="animate-spin" /> Generating...</>
                    : <><Sparkles size={15} /> Get AI Suggestions</>
                  }
                </GradientButton>
              </form>
            ) : (
              /* ── RESULTS ── */
              <div className="flex flex-col gap-[22px]">

                {/* Domain section header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-[9px]">
                    <div
                      className="flex items-center justify-center w-[30px] h-[30px] rounded-lg"
                      style={{ background: 'rgba(139,92,246,0.2)', border: '1px solid rgba(139,92,246,0.3)' }}
                    >
                      <Globe size={14} color="#c4b5fd" />
                    </div>
                    <span
                      className="text-[15px] font-bold"
                      style={{ background: 'linear-gradient(90deg,#c4b5fd,#67e8f9)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}
                    >Recommended Domains</span>
                  </div>
                  <span
                    className="text-[11px] text-[#4b5563] px-[10px] py-[3px] rounded-full"
                    style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
                  >{suggestions.domainSuggestions.length} domains</span>
                </div>

                {/* Domain cards */}
                <div className="flex flex-col gap-[10px]">
                  {suggestions.domainSuggestions.map((suggestion, index) => {
                    const pricing = domainPricing[suggestion.domain.toLowerCase()];
                    const isAvailable = pricing?.available ?? false;
                    return (
                      <div
                        key={index}
                        className={`px-[18px] py-4 rounded-[14px] transition-all duration-200 hover:border-violet-500/40 hover:shadow-[0_4px_24px_rgba(139,92,246,0.1)] fade-slide-${Math.min(index, 4)}`}
                        style={{
                          background: 'rgba(255,255,255,0.03)',
                          border: '1px solid rgba(255,255,255,0.1)',
                        }}
                      >
                        <div className="flex items-start justify-between gap-[14px]">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap mb-2">
                              <span className="text-[16px] font-bold text-[#f3f4f6] tracking-tight">
                                {suggestion.domain}
                              </span>
                              <span
                                className="px-2 py-[2px] rounded-[5px] text-[11px] font-bold text-[#a78bfa]"
                                style={{ background: 'rgba(139,92,246,0.15)', border: '1px solid rgba(139,92,246,0.25)' }}
                              >.{suggestion.tld}</span>
                              {isAvailable && (
                                <span
                                  className="flex items-center gap-[3px] px-2 py-[2px] rounded-[5px] text-[11px] font-bold text-[#34d399]"
                                  style={{ background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.25)' }}
                                >
                                  <CheckCircle2 size={10} /> Available
                                </span>
                              )}
                            </div>
                            <p className="m-0 mb-[10px] text-[#9ca3af] text-[13px] leading-relaxed">
                              {suggestion.reasoning}
                            </p>
                            {loadingPricing && !pricing && (
                              <div className="flex items-center gap-[6px] text-[#6b7280] text-[12px]">
                                <Loader2 size={13} className="spin text-violet-600" />
                                Loading pricing...
                              </div>
                            )}
                            {pricing && (
                              pricing.available
                                ? pricing.registerPrice && (
                                  <div
                                    className="inline-flex items-center gap-[5px] px-3 py-[5px] rounded-[7px]"
                                    style={{ background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.22)' }}
                                  >
                                    {(() => {
                                      const prefCurrency = (localStorage.getItem('preferredCurrency') as any) || 'AUD';
                                      const origCurrency = pricing.currency || 'USD';
                                      const priceInUSD = convertToUSD(pricing.registerPrice, origCurrency as any);
                                      const displayPrice = convertCurrency(priceInUSD, prefCurrency);
                                      return (
                                        <span className="text-[15px] font-extrabold text-[#f9fafb]">
                                          {formatPriceWithCurrency(displayPrice, prefCurrency)}
                                        </span>
                                      );
                                    })()}
                                    <span className="text-[11px] text-[#6b7280]">/ yr</span>
                                  </div>
                                )
                                : (
                                  <div
                                    className="inline-flex items-center gap-[5px] px-3 py-[5px] rounded-[7px]"
                                    style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}
                                  >
                                    <AlertCircle size={12} color="#f87171" />
                                    <span className="text-[12px] font-semibold text-[#f87171]">Unavailable</span>
                                  </div>
                                )
                            )}
                          </div>
                          {pricing?.available && (
                            <AnimatedButton
                              onClick={() => handleBuyDomain(suggestion.domain)}
                              variant="secondary"
                              className="shrink-0"
                            >
                              <ShoppingCart size={13} /> Buy Now
                            </AnimatedButton>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Related Services */}
                {suggestions.relatedServices && suggestions.relatedServices.length > 0 && (
                  <div>
                    <div className="flex items-center gap-[9px] mb-3">
                      <div
                        className="flex items-center justify-center w-[30px] h-[30px] rounded-lg"
                        style={{ background: 'rgba(34,211,238,0.15)', border: '1px solid rgba(34,211,238,0.25)' }}
                      >
                        <Zap size={14} color="#67e8f9" />
                      </div>
                      <span
                        className="text-[15px] font-bold"
                        style={{ background: 'linear-gradient(90deg,#67e8f9,#c4b5fd)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}
                      >Recommended Services</span>
                    </div>
                    <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
                      {suggestions.relatedServices.map((service, i) => (
                        <button
                          key={i}
                          onClick={() => { navigate(service.path); handleClose(); }}
                          className="flex items-center gap-[10px] px-[14px] py-3 rounded-[11px] cursor-pointer text-left transition-all duration-150 hover:border-violet-500/30 hover:bg-violet-500/[0.06]"
                          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
                        >
                          <ServiceIcon path={service.path} color="#a78bfa" />
                          <div className="flex-1 min-w-0">
                            <p className="m-0 mb-0.5 text-[#e5e7eb] text-[12px] font-semibold truncate">{service.title}</p>
                            <p className="m-0 text-[#4b5563] text-[11px] truncate">{service.description}</p>
                          </div>
                          <ChevronRight size={13} color="#374151" className="shrink-0" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Info cards */}
                <div
                  className="grid grid-cols-3 gap-[10px] pt-4"
                  style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}
                >
                  {[
                    { label: 'Business Tips', count: suggestions.businessRecommendations.length, unit: 'recommendations', icon: Lightbulb, color: '#a78bfa', action: () => setShowRecommendations(true) },
                    { label: 'Marketing Tips', count: suggestions.marketingTips.length, unit: 'strategies', icon: Megaphone, color: '#67e8f9', action: () => setShowMarketingTips(true) },
                    { label: 'Next Steps', count: suggestions.nextSteps.length, unit: 'items', icon: ArrowRight, color: '#6ee7b7', action: () => setShowNextSteps(true) },
                  ].map(({ label, count, unit, icon: Icon, color, action }) => (
                    <button
                      key={label}
                      onClick={action}
                      className="px-4 py-[15px] text-left rounded-[13px] cursor-pointer transition-all duration-200 hover:border-white/15 hover:bg-white/[0.04] hover:-translate-y-px"
                      style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)' }}
                    >
                      <div
                        className="flex items-center justify-center w-8 h-8 rounded-[9px] mb-[10px]"
                        style={{ background: `${color}18`, border: `1px solid ${color}25` }}
                      >
                        <Icon size={15} color={color} />
                      </div>
                      <p className="m-0 mb-[3px] text-[13px] font-bold text-[#e5e7eb]">{label}</p>
                      <p className="m-0 mb-3 text-[11px] text-[#4b5563]">{count} {unit}</p>
                      <div className="flex items-center gap-1 text-[12px] font-semibold" style={{ color }}>
                        <span>View all</span> <ArrowRight size={12} />
                      </div>
                    </button>
                  ))}
                </div>

                {/* Footer buttons */}
                <div className="flex gap-[10px] pb-1">
                  <AnimatedButton onClick={handleClose} variant="outline" className="flex-1">
                    Close
                  </AnimatedButton>
                  <AnimatedButton
                    onClick={() => { setSuggestions(null); setDomainPricing({}); setError(null); }}
                    variant="secondary"
                    className="flex-1"
                  >
                    ↺ New Suggestion
                  </AnimatedButton>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sub-popups */}
      <InfoPopup
        isOpen={showRecommendations} onClose={() => setShowRecommendations(false)}
        title="Business Recommendations" icon={Lightbulb}
        items={suggestions?.businessRecommendations || []} color="violet"
        relatedServices={suggestions?.relatedServices}
        onServiceClick={(path) => { navigate(path); handleClose(); }}
      />
      <InfoPopup
        isOpen={showMarketingTips} onClose={() => setShowMarketingTips(false)}
        title="Marketing Tips" icon={Megaphone}
        items={suggestions?.marketingTips || []} color="cyan"
        relatedServices={suggestions?.relatedServices}
        onServiceClick={(path) => { navigate(path); handleClose(); }}
      />
      <InfoPopup
        isOpen={showNextSteps} onClose={() => setShowNextSteps(false)}
        title="Next Steps" icon={ArrowRight}
        items={suggestions?.nextSteps || []} color="green"
        relatedServices={suggestions?.relatedServices}
        onServiceClick={(path) => { navigate(path); handleClose(); }}
      />
    </>
  );
}